
import { supabase } from "@/integrations/supabase/client";

export interface CongregationUser {
  id: string;
  name: string;
  email: string;
  braceletScans: number;
  journalEntries: number;
  lastActive: Date;
}

export async function fetchCongregationUsers(churchCode: string): Promise<CongregationUser[]> {
  try {
    const { data: bracelets, error: braceletError } = await supabase
      .from("bracelets")
      .select("referral_code, registered_user_id")
      .eq("church_code", churchCode);
      
    if (braceletError) throw braceletError;
    
    const userIds = bracelets
      ?.filter(b => b.registered_user_id)
      .map(b => b.registered_user_id) || [];
      
    if (userIds.length === 0) return [];
    
    const { data: profiles, error: profilesError } = await supabase
      .from("profiles")
      .select("id, full_name")
      .in("id", userIds);
      
    if (profilesError) throw profilesError;
    
    const { data: scanData, error: scanError } = await supabase
      .from("bracelet_scans")
      .select("bracelet_id")
      .eq("church_code", churchCode);
      
    if (scanError) throw scanError;
    
    const userScans = {};
    
    const { data: braceletUserMap, error: mapError } = await supabase
      .from("bracelets")
      .select("referral_code, registered_user_id")
      .eq("church_code", churchCode)
      .not("registered_user_id", "is", null);
      
    if (mapError) throw mapError;
    
    const braceletToUser = {};
    braceletUserMap?.forEach(item => {
      braceletToUser[item.referral_code] = item.registered_user_id;
    });
    
    const braceletScans = {};
    scanData?.forEach(scan => {
      braceletScans[scan.bracelet_id] = (braceletScans[scan.bracelet_id] || 0) + 1;
    });
    
    for (const braceletId in braceletScans) {
      const userId = braceletToUser[braceletId];
      if (userId) {
        userScans[userId] = (userScans[userId] || 0) + braceletScans[braceletId];
      }
    }
    
    const userJournalEntries = {};
    for (const userId of userIds) {
      const { count: journalCount } = await supabase
        .from("journal_entries")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId);
        
      if (journalCount !== null) {
        userJournalEntries[userId] = journalCount;
      }
    }
    
    const usersWithActivity = profiles.map(profile => ({
      id: profile.id,
      name: profile.full_name,
      email: `user_${profile.id.substring(0, 8)}@example.com`,
      braceletScans: userScans[profile.id] || 0,
      journalEntries: userJournalEntries[profile.id] || 0,
      lastActive: new Date()
    }));
    
    return usersWithActivity;
  } catch (error) {
    console.error("Error fetching congregation users:", error);
    return [];
  }
}
